/**
 * @cfleet/auth - useProtectedRoute hook
 *
 * Provides shared route protection logic for both web and React Native apps.
 * This hook contains pure logic with no platform-specific dependencies.
 *
 * @example
 * ```tsx
 * // Basic usage - just require authentication
 * const { isLoading, isAuthorized, authorizationError } = useProtectedRoute();
 *
 * // Role-based access (OR logic - user needs any one of these roles)
 * const { isAuthorized } = useProtectedRoute({
 *   requiredRoles: ['Admin', 'Manager'],
 * });
 *
 * // Permission-based access (AND logic by default)
 * const { isAuthorized } = useProtectedRoute({
 *   requiredPermissions: ['manageDrivers', 'viewClients'],
 * });
 *
 * // Combined role and permission check
 * const { isAuthorized } = useProtectedRoute({
 *   requiredRoles: ['Admin'],
 *   requiredPermissions: ['viewAudit'],
 * });
 * ```
 */

import { useMemo } from 'react';
import { useAuth } from '../AuthProvider';
import type { UserRole, UserPermissions, User } from '../types';

/**
 * Options for route protection
 */
export interface UseProtectedRouteOptions {
  /**
   * Required roles for access (OR logic - user needs ANY of these roles)
   * If not provided, no role check is performed
   */
  requiredRoles?: UserRole[];

  /**
   * Required permissions for access
   * If not provided, no permission check is performed
   */
  requiredPermissions?: (keyof UserPermissions)[];

  /**
   * Whether all permissions are required (AND) or just one (OR)
   * @default true (AND logic)
   */
  requireAllPermissions?: boolean;
}

/**
 * Result from the useProtectedRoute hook
 */
export interface UseProtectedRouteResult {
  /** Whether authentication state is being loaded */
  isLoading: boolean;

  /** Whether the user is authenticated */
  isAuthenticated: boolean;

  /**
   * Whether the user is authorized to access the route.
   * This is true only if the user is authenticated AND
   * passes all role/permission checks.
   */
  isAuthorized: boolean;

  /** The current authenticated user (null if not authenticated) */
  user: User | null;

  /**
   * The authorization error type, if any:
   * - 'unauthenticated': User is not logged in
   * - 'unauthorized': User is logged in but lacks required role/permission
   * - null: No error (authorized or still loading)
   */
  authorizationError: 'unauthenticated' | 'unauthorized' | null;
}

/**
 * Hook for protecting routes based on authentication and authorization.
 *
 * This hook provides pure logic for route protection that can be used
 * in both web (react-router-dom) and native (expo-router) contexts.
 * Platform-specific rendering is handled by wrapper components.
 *
 * @param options - Protection options (roles, permissions)
 * @returns Protection state including loading, authorization status, and errors
 */
export function useProtectedRoute(
  options: UseProtectedRouteOptions = {}
): UseProtectedRouteResult {
  const { isAuthenticated, isLoading, user, hasRole, hasPermission } = useAuth();
  const { requiredRoles, requiredPermissions, requireAllPermissions = true } = options;

  /**
   * Determine if user is authorized based on role and permission requirements.
   * Authorization requires:
   * 1. User must be authenticated
   * 2. User must have at least one required role (if roles specified)
   * 3. User must have required permissions (AND or OR based on config)
   */
  const isAuthorized = useMemo(() => {
    // Not authorized if not authenticated
    if (!isAuthenticated || !user) {
      return false;
    }

    // Check roles (OR logic - user needs at least one)
    if (requiredRoles && requiredRoles.length > 0) {
      const hasAnyRole = requiredRoles.some((role) => hasRole(role));
      if (!hasAnyRole) {
        return false;
      }
    }

    // Check permissions
    if (requiredPermissions && requiredPermissions.length > 0) {
      if (requireAllPermissions) {
        // AND logic - user needs ALL permissions
        const hasAllPermissions = requiredPermissions.every((perm) => hasPermission(perm));
        if (!hasAllPermissions) {
          return false;
        }
      } else {
        // OR logic - user needs at least ONE permission
        const hasAnyPermission = requiredPermissions.some((perm) => hasPermission(perm));
        if (!hasAnyPermission) {
          return false;
        }
      }
    }

    return true;
  }, [
    isAuthenticated,
    user,
    requiredRoles,
    requiredPermissions,
    requireAllPermissions,
    hasRole,
    hasPermission,
  ]);

  /**
   * Determine the authorization error type.
   * This is computed separately to allow loading state to not trigger errors.
   */
  const authorizationError = useMemo((): 'unauthenticated' | 'unauthorized' | null => {
    // No error while loading
    if (isLoading) {
      return null;
    }

    // Not authenticated - redirect to login
    if (!isAuthenticated) {
      return 'unauthenticated';
    }

    // Authenticated but not authorized - show forbidden
    if (!isAuthorized) {
      return 'unauthorized';
    }

    // All checks passed
    return null;
  }, [isLoading, isAuthenticated, isAuthorized]);

  return {
    isLoading,
    isAuthenticated,
    isAuthorized,
    user,
    authorizationError,
  };
}
